import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Quote } from "@/lib/api";
import {
  ArrowLeft,
  FileSpreadsheet,
  Loader2,
  Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const Field = ({ label, children }) => (
  <label className="block">
    <div className="swiss-label mb-1">{label}</div>
    {children}
  </label>
);

const CaptureQuote = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const q = state?.quote || {};

  const [form, setForm] = useState({
    customer_name: q.customer_name || "",
    quote_number: q.quote_number || "",
    total_amount: q.total_amount != null ? String(q.total_amount) : "",
    quote_date: q.quote_date || new Date().toISOString().slice(0, 10),
    install_date: q.install_date || "",
    notes: q.notes || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const total = Number(form.total_amount) || 0;
  const stages = [
    {
      label: "Stage 1 · Install deposit 50%",
      amount: total * 0.5,
      hint: "Due when the quote is signed",
    },
    {
      label: "Stage 2 · Install deposit 30%",
      amount: total * 0.3,
      hint: form.install_date
        ? `Due on install (${form.install_date})`
        : "Due on install (+14 days from quote)",
    },
    {
      label: "Stage 3 · Install deposit 20%",
      amount: total * 0.2,
      hint: "Due on completion (+14 days from install)",
    },
  ];

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error("Customer name is required");
    if (!total || total <= 0) return toast.error("Total amount must be > 0");
    setSaving(true);
    try {
      await Quote.createIncomes({
        customer_name: form.customer_name.trim(),
        quote_number: form.quote_number || null,
        total_amount: total,
        quote_date: form.quote_date || null,
        install_date: form.install_date || null,
        notes: form.notes || null,
      });
      toast.success("3 incomes created (50 / 30 / 20%).");
      navigate("/income");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="capture-quote" className="fade-rise">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="swiss-label mb-3 inline-flex items-center gap-2">
        <FileSpreadsheet className="h-3 w-3" /> Quote
      </div>
      <h1 className="font-display font-black text-4xl tracking-tight leading-tight">
        New Quote → 3 Incomes
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Billy will create three Income entries — 50% on signing, 30% on install,
        20% on completion — all tagged to this customer.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
        <div className="space-y-5">
          <Field label="Customer name">
            <Input
              data-testid="input-customer"
              className="swiss-input"
              value={form.customer_name}
              onChange={update("customer_name")}
              placeholder="e.g. Smith — 12 Hill St"
            />
          </Field>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Quote #">
              <Input
                data-testid="input-quote-number"
                className="swiss-input"
                value={form.quote_number}
                onChange={update("quote_number")}
                placeholder="Q1234"
              />
            </Field>
            <Field label="Total (incl. GST)">
              <Input
                data-testid="input-total"
                className="swiss-input mono"
                inputMode="decimal"
                value={form.total_amount}
                onChange={update("total_amount")}
                placeholder="0.00"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <Field label="Quote date">
              <Input
                data-testid="input-quote-date"
                type="date"
                className="swiss-input"
                value={form.quote_date}
                onChange={update("quote_date")}
              />
            </Field>
            <Field label="Install date">
              <Input
                data-testid="input-install-date"
                type="date"
                className="swiss-input"
                value={form.install_date}
                onChange={update("install_date")}
              />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <Textarea
              data-testid="input-notes"
              className="swiss-input resize-none"
              rows={3}
              value={form.notes}
              onChange={update("notes")}
              placeholder="What was quoted?"
            />
          </Field>
        </div>

        {/* 3-stage preview */}
        <div>
          <div className="swiss-label mb-3">Preview · 3 incomes will be created</div>
          <div className="space-y-3">
            {stages.map((s, i) => (
              <div
                key={i}
                data-testid={`stage-preview-${i}`}
                className="border-2 border-black p-4 bg-white"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div>
                    <div className="font-display font-extrabold text-lg leading-none">
                      {s.label}
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 mt-2">
                      {s.hint}
                    </div>
                  </div>
                  <div className="font-display font-black text-2xl mono">
                    {total > 0 ? fmt(s.amount) : "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-right mt-3">
            <div className="swiss-label">Total of stages</div>
            <div className="font-display font-black text-3xl mono mt-1">
              {fmt(total)}
            </div>
          </div>

          <button
            data-testid="save-btn"
            onClick={save}
            disabled={saving || !total || !form.customer_name.trim()}
            className="w-full mt-6 border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-5 py-4 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Create 3 incomes
          </button>
        </div>
      </div>
    </div>
  );
};

export default CaptureQuote;
